import { useMemo } from "react";
import { useEditorStore } from "../store";

const TEMPLATE_VAR_RE = /\{\{([A-Za-z][A-Za-z0-9_]*)\}\}/g;
const VAULT_PREFIX_RE = /^(secret|config):/;

/** Extract plain variable names from query text (excludes {{secret:...}} and {{config:...}}). */
function extractVariableNames(text: string): string[] {
  const names = new Set<string>();
  for (const match of text.matchAll(TEMPLATE_VAR_RE)) {
    const name = match[1]!;
    if (!VAULT_PREFIX_RE.test(name)) names.add(name);
  }
  return [...names];
}

interface MongoVariablesPaneProps {
  queryText: string;
}

export function MongoVariablesPane({ queryText }: MongoVariablesPaneProps) {
  const variables = useEditorStore((s) => s.variables);
  const setVariables = useEditorStore((s) => s.setVariables);

  const variableNames = useMemo(() => extractVariableNames(queryText), [queryText]);

  // Parse current variables as key-value record
  const variableValues = useMemo<Record<string, string>>(() => {
    if (!variables.trim()) return {};
    try {
      const parsed = JSON.parse(variables);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
        const result: Record<string, string> = {};
        for (const [k, v] of Object.entries(parsed)) {
          result[k] = typeof v === "string" ? v : JSON.stringify(v);
        }
        return result;
      }
    } catch { /* not valid JSON yet */ }
    return {};
  }, [variables]);

  const updateVariable = (name: string, value: string) => {
    const next = { ...variableValues, [name]: value };
    setVariables(JSON.stringify(next, null, 2));
  };

  if (variableNames.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-center">
        <div className="max-w-xs">
          <p className="text-xs text-zinc-500">
            Add <code className="rounded bg-surface-overlay px-1.5 py-0.5 text-accent">{"{{variableName}}"}</code> to your query to create parameters.
          </p>
          <p className="mt-2 text-[10px] text-zinc-600">
            Use <code className="text-zinc-500">{"{{secret:KEY}}"}</code> or <code className="text-zinc-500">{"{{config:KEY}}"}</code> for vault references.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0 overflow-auto p-2">
      {variableNames.map((name) => (
        <div key={name} className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-surface-overlay/50">
          <label className="w-32 shrink-0 truncate font-mono text-xs text-accent" title={name}>
            {`{{${name}}}`}
          </label>
          <input
            type="text"
            value={variableValues[name] ?? ""}
            onChange={(e) => updateVariable(name, e.target.value)}
            placeholder="value"
            className="flex-1 rounded border border-zinc-700 bg-surface px-2 py-1 font-mono text-xs text-zinc-200 placeholder:text-zinc-600 focus:border-accent focus:outline-none"
          />
        </div>
      ))}
    </div>
  );
}
